/**
 * NewsTicker - Scrolling news ticker for flight completions, alerts, and events
 * Used in both OpsCenter and WorldMap
 */

import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Helper to format flight phase
const formatPhase = (phase, t) => {
  const phaseKeys = {
    scheduled: "phaseScheduled",
    boarding: "phaseBoarding",
    taxi_out: "phaseTaxiOut",
    takeoff: "phaseTakeoff",
    cruise: "phaseCruise",
    descent: "phaseDescent",
    approach: "phaseApproach",
    taxi_in: "phaseTaxiIn",
    completed: "phaseCompleted",
    cancelled: "phaseCancelled",
    in_flight: "phaseInFlight",
  };
  const key = phaseKeys[phase];
  return key ? t(`newsTicker.${key}`) : phase;
};

// Get reputation tier from score (0-15000+ scale)
const getReputationTier = (score) => {
  if (score >= 15000) return 5;
  if (score >= 7000) return 4;
  if (score >= 3000) return 3;
  if (score >= 1000) return 2;
  return 1;
};

export default function NewsTicker({ worldState }) {
  const { t } = useTranslation();

  // Fallback news when nothing interesting is happening
  const FALLBACK_NEWS = [
    { type: "info", text: t('newsTicker.fallback1') },
    { type: "info", text: t('newsTicker.fallback2') },
    { type: "info", text: t('newsTicker.fallback3') },
  ];

  // Build dynamic news feed from multiple sources
  const newsItems = useMemo(() => {
    const items = [];
    const flights = worldState?.flights || [];
    const airline = worldState?.airline || {};
    const macro = worldState?.macro || {};

    // 0. LIVE WEATHER ALERTS (highest priority)
    const airportWeather = worldState?.airportWeather || {};
    for (const [code, weather] of Object.entries(airportWeather)) {
      const impact = weather?.impact;
      if (impact?.severity === 'grounded') {
        items.push({
          type: 'critical',
          text: t('newsTicker.weatherGroundStop', { code, reason: impact.reason }),
        });
      } else if (impact?.severity === 'delay' && impact.delayMinutes >= 30) {
        items.push({
          type: 'warning',
          text: t('newsTicker.weatherDelay', { code, minutes: impact.delayMinutes, reason: impact.reason }),
        });
      }
    }

    // 1. RECENT FLIGHT COMPLETIONS (last 5)
    const completedFlights = flights
      .filter(f => f.phase === "completed" && f.revenue)
      .sort((a, b) => new Date(b.completedAt || 0) - new Date(a.completedAt || 0))
      .slice(0, 5);

    for (const f of completedFlights) {
      const profit = (f.revenue || 0) - (f.costs || 0);
      const profitStr = `${profit >= 0 ? '+' : ''}$${(profit/1000).toFixed(1)}K`;
      items.push({
        type: profit >= 0 ? "profit" : "loss",
        text: t('newsTicker.flightCompleted', { flight: f.flightNumber, route: `${f.origin}-${f.destination}`, profit: profitStr }),
      });
    }

    // 2. CANCELLATIONS (critical news)
    const cancelledFlights = flights
      .filter(f => f.status === "cancelled" || f.phase === "cancelled")
      .slice(0, 3);

    for (const f of cancelledFlights) {
      const reason = f.cancellationReason || t('newsTicker.noCancellationReason');
      items.push({
        type: "critical",
        text: t('newsTicker.flightCancelled', { route: `${f.origin}-${f.destination}`, reason }),
      });
    }

    // 3. DELAYED FLIGHTS (in progress)
    const delayedFlights = flights
      .filter(f => f.totalDelayMinutes > 10 && f.phase !== "completed" && f.phase !== "cancelled")
      .slice(0, 3);

    for (const f of delayedFlights) {
      items.push({
        type: "warning",
        text: t('newsTicker.flightDelayed', { flight: f.flightNumber, minutes: f.totalDelayMinutes, phase: formatPhase(f.phase, t) }),
      });
    }

    // 4. MACRO ENGINE NEWS / ZOMBIE NEWS
    const macroNews = worldState?.newsFeed || [];
    for (const news of macroNews.slice(0, 5)) {
      items.push({
        type: news.severity || "info",
        text: news.headline || news.message || news.text,
      });
    }

    // 5. FUEL CHAOS (Market Maker)
    const chaos = worldState?.fuelMarketChaos;
    if (chaos?.active) {
      items.push({
        type: "critical",
        text: t('newsTicker.fuelChaos', { min: chaos.priceMin.toFixed(2), max: chaos.priceMax.toFixed(2) }),
      });
    }

    // 6. DAILY P&L SUMMARY
    const todayCompleted = completedFlights.length;
    const todayRevenue = completedFlights.reduce((sum, f) => sum + (f.revenue || 0), 0);
    const todayCosts = completedFlights.reduce((sum, f) => sum + (f.costs || 0), 0);
    const todayProfit = todayRevenue - todayCosts;

    if (todayCompleted > 0) {
      const profitStr = `${todayProfit >= 0 ? '+' : ''}$${(todayProfit/1000).toFixed(0)}K`;
      items.push({
        type: todayProfit >= 0 ? "profit" : "loss",
        text: t('newsTicker.todaySummary', { count: todayCompleted, revenue: (todayRevenue/1000).toFixed(0), profit: profitStr }),
      });
    }

    // 7. CASH POSITION WARNING
    const cash = airline.cash || 0;
    if (cash < 5000000) {
      items.push({
        type: "critical",
        text: t('newsTicker.cashCritical', { amount: (cash/1000000).toFixed(2) }),
      });
    }

    // 8. REPUTATION STATUS
    const repScore = worldState?.reputation?.currentScore || 0;
    const repTier = getReputationTier(repScore);
    if (repScore >= 15000) {
      items.push({
        type: "good",
        text: t('newsTicker.eliteStatus', { score: repScore.toLocaleString() }),
      });
    }

    // 9. Fallback if nothing interesting
    if (items.length < 3) {
      items.push(...FALLBACK_NEWS.slice(0, 5 - items.length));
    }

    return items;
  }, [worldState?.flights, worldState?.newsFeed, worldState?.airline, worldState?.macro, worldState?.reputation, worldState?.airportWeather, worldState?.fuelMarketChaos, t, FALLBACK_NEWS]);

  return (
    <div className="pr-4 pb-4">
      <div className="overflow-hidden" style={{ backgroundColor: "#1a0a0a", borderTop: "1px solid #3a1a1a", borderRight: "1px solid #3a1a1a", borderBottom: "1px solid #3a1a1a" }}>
        <div className="flex items-center">
          <div className="px-3 py-2 flex-shrink-0" style={{ backgroundColor: "#2a0a0a", borderRight: "1px solid #3a1a1a" }}>
            <span style={{ color: "#ef4444", fontWeight: "bold", fontSize: "12px" }}>{t('newsTicker.news')}</span>
          </div>
          <div className="flex-1 overflow-hidden">
            <div className="ticker-wrapper">
              <div className="ticker-content">
                {/* Duplicate content for seamless loop */}
                {[...newsItems, ...newsItems].map((item, idx) => {
                  // Color based on news type
                  let color = "#f87171"; // default red
                  if (item.type === "profit" || item.type === "good") color = "#22c55e";
                  else if (item.type === "warning" || item.type === "caution") color = "#eab308";
                  else if (item.type === "critical" || item.type === "loss") color = "#ef4444";
                  else if (item.type === "info") color = "#60a5fa";
                  else if (item.type === "zombie") color = "#a855f7";

                  // Add BREAKING NEWS prefix for zombie news that doesn't already have it
                  let displayText = item.text;
                  if (item.type === "zombie" && !item.text.startsWith("BREAKING")) {
                    displayText = t('newsTicker.breakingNews') + " " + item.text;
                  }

                  return (
                    <span key={idx} className="ticker-item" style={{ color }}>
                      {displayText}
                      <span className="ticker-separator">|</span>
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Ticker animation styles */}
      <style>{`
        .ticker-wrapper {
          width: 100%;
          overflow: hidden;
        }
        .ticker-content {
          display: inline-block;
          white-space: nowrap;
          animation: ticker-scroll 60s linear infinite;
          padding: 8px 0;
        }
        .ticker-item {
          display: inline;
          font-size: 14px;
        }
        .ticker-separator {
          display: inline-block;
          margin: 0 24px;
          color: #4a2a2a;
        }
        @keyframes ticker-scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        .ticker-content:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
}
